import { uuid } from '@oskecare/fe-utilities/scripts/uuid';

const GTM_URL = 'https://www.googletagmanager.com/gtm.js';
const MHUB_CLIENT_ID_COOKIE = 'mHubClientId';

/**
 * Google Tag Manager integration with micro-frontend architecture.
 *
 * Based on the GTM snippet: https://developers.google.com/tag-manager/quickstart
 * Does the same sort of initialization, but also provides EventBus API to push DataLayer events.
 *
 * @property {MessageBus} _messageBus
 * @property {AuthManager} _authManager
 * @property {object} _config
 * @property {?object} _mhub
 */
export default class TagManager {
  /**
   * Constructor with dependency injection.
   *
   * @param {MessageBus} messageBus
   * @param {AuthManager} authManager
   * @param {object} config
   * @private
   */
  constructor({ messageBus, authManager, config }) {
    this._messageBus = messageBus;
    this._authManager = authManager;
    this._config = config;
    this._mhub = null;
  }

  /**
   * Initialization of mHub GTM container.
   */
  initialize() {
    window.mhub = window.mhub || [];
    this._mhub = window.mhub;
    const initialPushEvent = { 'gtm.start': new Date().getTime(), event: 'gtm.js' };
    this.mhubPush(initialPushEvent);
    if (this._config.allowGtm) {
      TagManager._loadGtmScript({ id: this._config.mhubCode, l: 'mhub' });
    }
    this._registerEventBusListeners();
    this._registerPersonalizationListener();
    this._authManager.attachTagManager(this);
  }

  /**
   * Push event to mhub.
   *
   * @param {object} event
   */
  mhubPush(event) {
    this._mhub.push(event);
  }

  /**
   * Push GTM pageView event along with user event to mhub container
   *
   * @param {string} type
   * @param {?string} section
   * @param {string} trail
   * @param {?object} detail
   * @param {?object} checkout
   */
  mhubPushPageView({ type, section = null, trail, detail = null, checkout = null }) {
    // TODO: rewrite to use `CustomerDataManager` and `CustomerData` instead of using `AuthManager` and `userData` directly
    const userData = this._authManager.getUserData();
    this._mhub.push({
      page: {
        locale: 'sk-SK',
        type: type || 'other',
        kind: 'B2C',
        category: 'no_category',
        section,
        trail,
        ...(detail && { detail }),
        ...(checkout && { checkout }),
      },
    });
    this._mhub.push({
      user: {
        id: userData.subAccount,
        clientId: TagManager._getClientId(),
        loggedId: userData.subAccount,
        snId: userData.subId,
        paymentType: userData.isPrepaid ? 'prepaid' : 'postpaid',
        role: userData.isCustomerAdmin ? 'customerAdmin' : null,
      },
    });
    this._mhub.push({ event: 'page' });
  }

  /**
   * Loads the GTM script. Identical implementation to GTM snippet, unobfuscated.
   *
   * @param {object} queryParams - params containing GTM container id and other optional filters
   * @private
   */
  static _loadGtmScript(queryParams) {
    const firstScript = document.getElementsByTagName('script')[0];
    const gtmScript = document.createElement('script');
    gtmScript.async = true;
    const params = new URLSearchParams(queryParams);
    gtmScript.src = `${GTM_URL}?${params.toString()}`;
    firstScript.parentNode.insertBefore(gtmScript, firstScript);
  }

  /**
   * Register EventBus listeners, to better integrate with micro-frontend architecture.
   *
   * TODO: consider removal of messageBus listeners as they seem unused
   *
   * @private
   */
  _registerEventBusListeners() {
    this._messageBus.subscribe('mhub', 'push', event => {
      this.mhubPush(event);
    });
    this._messageBus.subscribe('mhub', 'pushPageView', event => {
      this.mhubPushPageView(event);
    });
  }

  _registerPersonalizationListener() {
    window.personalize = (target, content) => {
      this._messageBus.publish('personalization', target, content, true);
    };
  }

  /**
   * Checks whether mhub client id cookie is set and returns its value if so. Otherwise generates new cookie and returns its value.
   *
   * @returns {string}
   * @private
   */
  static _getClientId() {
    const clientIdMatch = document.cookie.match(
      new RegExp(`(^| )${MHUB_CLIENT_ID_COOKIE}=([^;]+)`),
    );
    if (clientIdMatch) {
      return clientIdMatch[2];
    }
    const newClientId = uuid();

    const domainMatch = window.location.hostname.match(/^(www\.)?(orange\.(test|sk))$/);
    if (!domainMatch) {
      // Unless we're on orange.sk (prod) or orange.test (test) domains, e.g. dev/localhost, do not create cookie
      return newClientId;
    }
    const domain = `.${domainMatch[2]}`; // Domain has a leading dot by agreement
    const nextYear = new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toGMTString();

    const cookie = [
      `${MHUB_CLIENT_ID_COOKIE}=${newClientId}`,
      `Expires=${nextYear}`,
      `Domain=${domain}`,
      `Path=/`,
      'SameSite=Strict',
      'Secure',
    ];

    document.cookie = cookie.join(';');
    return newClientId;
  }
}
